import { ModToast, ModToastButton } from '@moderneinc/design-system-components';
import { semanticColors } from '@moderneinc/design-system-tokens';
import { useRouter } from 'next/router';
import React, { type FunctionComponent, useEffect, useState } from 'react';
import { ADMIN_SETTINGS_PATH } from '../../constants/general';
import { safeLocalStorage } from '../../helpers/local-storage.helper';
import { sanitizeDisplayName } from '../../helpers/string.helper';
import { useAuthorization } from '../../hooks/use-authorization.hooks';
import { useCapabilities } from '../../hooks/use-capabilities.hooks';

const STORAGE_KEY_PREFIX = 'profiling-toast-dismissed:';

export const ProfilingAdminToast: FunctionComponent = () => {
  const router = useRouter();
  const { isAdministrator = false } = useAuthorization();
  const { profiling } = useCapabilities();
  const sessionKey = profiling.session
    ? String(profiling.session.startedAt)
    : null;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!sessionKey) {
      setDismissed(false);
      return;
    }
    setDismissed(
      safeLocalStorage.getItem(`${STORAGE_KEY_PREFIX}${sessionKey}`) === 'true'
    );
  }, [sessionKey]);

  const handleDismiss = () => {
    setDismissed(true);
    if (sessionKey) {
      // A failed write only costs the dismissal its durability; the toast still
      // stays hidden for this session.
      safeLocalStorage.setItem(`${STORAGE_KEY_PREFIX}${sessionKey}`, 'true');
    }
  };

  if (
    !isAdministrator ||
    !profiling.isActive ||
    !profiling.session ||
    dismissed
  ) {
    return null;
  }

  const { user } = profiling.session;
  const userName = sanitizeDisplayName(user.username || user.email);
  const message = `${userName} has enabled system profiling. Requests may be slower than usual until it's turned off.`;

  return (
    <ModToast
      intent="info"
      type="outlined"
      title="System profiling is on"
      message={message}
      showClose
      onClose={handleDismiss}
      sx={{ maxWidth: 400 }}
      actions={
        <React.Fragment>
          <ModToastButton
            onClick={handleDismiss}
            sx={{ '&&': { color: semanticColors.color.text.link } }}
          >
            Dismiss
          </ModToastButton>
          <ModToastButton
            primary
            onClick={() => router.push(ADMIN_SETTINGS_PATH)}
          >
            Go to settings to turn off
          </ModToastButton>
        </React.Fragment>
      }
    />
  );
};
